import { inject, Injectable, InjectionToken, ViewContainerRef } from '@angular/core';

import { lastValueFrom, tap } from 'rxjs';

import { I18nService } from '@core/i18n-legacy';
import { TellentGroupKind, TellentMember } from '@core/tellent-types';
import { resolveMemberFullName } from '@core/tellent-util';
import { DialogService, DialogWidth, NotificationsController } from '@core/user-interface-legacy';

import { EditMemberGroupsTabService } from './edit-member-group-tab.service';
import { RemoveTeamMemberFromGroupConfirmationDialogComponent } from '../../remove-team-member-from-group-confirmation-dialog/remove-team-member-from-group-confirmation-dialog.component';
import { SelectGroupsDialogComponent } from '../../select-groups-dialog/select-groups-dialog.component';

export const GROUP_KIND = new InjectionToken<TellentGroupKind>('GROUP_KIND');

@Injectable()
export class EditMemberGroupsTabController {
  private readonly kind = inject(GROUP_KIND);
  private readonly viewContainerRef = inject(ViewContainerRef);
  private readonly i18n = inject(I18nService);
  private readonly editMemberGroupsTabService = inject(EditMemberGroupsTabService);
  private readonly dialogService = inject(DialogService);
  private readonly notificationsController = inject(NotificationsController);
  private readonly translationKeys = {
    [TellentGroupKind.Group]: {
      groupAdded: 'tlc.members.edit_member.groups_tab.groups_added',
      groupRemoved: 'tlc.members.edit_member.groups_tab.group_removed',
    },
    [TellentGroupKind.Department]: {
      groupAdded: 'tlc.members.edit_member.departments_tab.departments_added',
      groupRemoved: 'tlc.members.edit_member.departments_tab.department_removed',
    },
  };

  public memberData(guid: string) {
    return this.editMemberGroupsTabService.selectGroupDetailsData(guid);
  }

  public openAddGroupsDialog(member: TellentMember): void {
    this.dialogService.openCustom(
      SelectGroupsDialogComponent,
      { width: DialogWidth.SmallX },
      {
        saveAction: (groupsGuids: string[]) => this.addGroupsToMember(member.guid, groupsGuids),
        groupsIds: member.teams!.map(group => group.guid),
        memberName: resolveMemberFullName(member),
      },
      this.viewContainerRef,
    );
  }

  public openRemoveMemberDialog(
    group: { name: string; guid: string; kind: TellentGroupKind },
    member: TellentMember,
  ): void {
    this.dialogService.openCustom(
      RemoveTeamMemberFromGroupConfirmationDialogComponent,
      { width: DialogWidth.SmallXX },
      {
        member: {
          ...member,
          fullName: resolveMemberFullName(member),
        },
        groupName: group.name,
        groupKind: group.kind,
        saveAction: () => this.removeMembersFromGroup(member.guid, [group.guid]),
      },
    );
  }

  public addGroupsToMember(memberGuid: string, groupsGuids: string[]) {
    return lastValueFrom(
      this.editMemberGroupsTabService.addMembersToGroup(memberGuid, groupsGuids).pipe(
        tap(result => {
          if (result.isSuccess) {
            this.notificationsController.success(
              this.i18n.translate(this.translationKeys[this.kind].groupAdded),
            );
          }
        }),
      ),
    );
  }

  public removeMembersFromGroup(memberGuid: string, groupsGuids: string[]) {
    return lastValueFrom(
      this.editMemberGroupsTabService.removeMembersFromGroup(memberGuid, groupsGuids).pipe(
        tap(result => {
          if (result.isSuccess) {
            this.notificationsController.success(
              this.i18n.translate(this.translationKeys[this.kind].groupRemoved),
            );
          }
        }),
      ),
    );
  }
}
